import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Box, Button, Stack, TextField, Typography,
} from '@mui/material';
import { BsShieldCheck } from 'react-icons/bs';

import axiosInstance from '../../../axiosInstance';
import ChampMotDePasse from '../../../components/Auth/ChampMotDePasse';
import EcranAuth from '../../../components/Auth/EcranAuth';
import { EXIGENCE, defautDuMotDePasse } from '../../../components/Auth/regles';

const CLE_RECAPTCHA = '6Lfft_0pAAAAAESWF337nK-0GOR5XIMhNDoo1Mh5';

/**
 * Demande un jeton reCAPTCHA v3 au moment de l'envoi.
 *
 * Un jeton pris au chargement de la page est périmé au bout de deux minutes :
 * celui qui hésite sur son mot de passe voyait son inscription refusée. Un
 * jeton absent (script bloqué) part tel quel : le serveur refuse alors avec
 * un message qui demande de lever le bloqueur.
 */
async function jetonRecaptcha() {
  try {
    return await window.grecaptcha.execute(CLE_RECAPTCHA, { action: 'signup' });
  } catch {
    return null;
  }
}

function Signup() {
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoye, setEnvoye] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const [piege, setPiege] = useState('');

  useEffect(() => {
    if (document.getElementById('recaptcha')) return;
    const script = document.createElement('script');
    script.id = 'recaptcha';
    script.src = `https://www.google.com/recaptcha/api.js?render=${CLE_RECAPTCHA}`;
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const defaut = defautDuMotDePasse(motDePasse);
  const discordant = confirmation !== '' && motDePasse !== confirmation;
  const complet = email && motDePasse && !defaut && motDePasse === confirmation;

  const soumettre = async (e) => {
    e.preventDefault();
    setOccupe(true);
    setErreur('');
    try {
      await axiosInstance.post('signup/', {
        email: email.trim(),
        password: motDePasse,
        recaptcha: await jetonRecaptcha(),
        site_web: piege,
      });
      setEnvoye(true);
    } catch (error) {
      const donnees = error.response?.data;
      setErreur(donnees?.error || donnees?.password?.[0] || donnees?.email?.[0]
        || "L'inscription n'a pas abouti. Réessayez.");
    } finally {
      setOccupe(false);
    }
  };

  if (envoye) {
    return (
      <EcranAuth titre="Vérifiez vos emails">
        <Stack spacing={3}>
          <Alert severity="success">
            Votre compte est créé. Un message vient de partir à
            {' '}<strong>{email}</strong> : il contient le lien qui l'active.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Sans ce lien, la connexion sera refusée. Si le message n'arrive
            pas d'ici quelques minutes, regardez dans les indésirables.
          </Typography>
          <Button component={Link} to="/login" variant="outlined">
            Retour à la connexion
          </Button>
        </Stack>
      </EcranAuth>
    );
  }

  return (
    <EcranAuth
      titre="Créer un compte"
      sous_titre="Quelques secondes suffisent"
      bas={(
        <Typography variant="body2" color="text.secondary">
          Vous avez déjà un compte ?{' '}
          <Box component={Link} to="/login"
               sx={{ color: 'primary.main', fontWeight: 600 }}>
            Se connecter
          </Box>
        </Typography>
      )}
    >
      <Box component="form" onSubmit={soumettre} noValidate>
        {erreur && <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert>}

        {/* Pot de miel : hors écran et hors tabulation, aucun humain ne le
            remplit. Le robot qui renseigne tous les champs se trahit. */}
        <Box aria-hidden="true"
             sx={{ position: 'absolute', left: '-10000px', width: 1, height: 1,
                   overflow: 'hidden' }}>
          <label htmlFor="site_web">Site web</label>
          <input id="site_web" name="site_web" type="text" tabIndex={-1}
                 autoComplete="off" value={piege}
                 onChange={(e) => setPiege(e.target.value)} />
        </Box>

        <Stack spacing={2.5}>
          <TextField
            label="Adresse email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            required
            helperText="C'est aussi votre identifiant de connexion."
          />

          <ChampMotDePasse
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            autoComplete="new-password"
            required
            fullWidth
            error={Boolean(defaut)}
            helperText={defaut || EXIGENCE}
          />

          <ChampMotDePasse
            label="Confirmer le mot de passe"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            autoComplete="new-password"
            required
            fullWidth
            error={discordant}
            helperText={discordant ? 'Les deux saisies diffèrent.' : ' '}
          />

          <Button type="submit" variant="contained" size="large"
                  disabled={occupe || !complet}>
            {occupe ? 'Création…' : 'Créer mon compte'}
          </Button>

          <Typography variant="caption" color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75,
                            justifyContent: 'center' }}>
            <BsShieldCheck aria-hidden="true" /> Protégé par reCAPTCHA
          </Typography>

          <Typography variant="caption" color="text.secondary"
                      sx={{ textAlign: 'center', lineHeight: 1.6 }}>
            En créant un compte, vous acceptez nos{' '}
            <Box component={Link} to="/conditions" sx={{ color: 'inherit' }}>
              conditions d'utilisation
            </Box>{' '}et notre{' '}
            <Box component={Link} to="/confidentialite" sx={{ color: 'inherit' }}>
              politique de confidentialité
            </Box>.
          </Typography>
        </Stack>
      </Box>
    </EcranAuth>
  );
}

export default Signup;
